import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps } from '@mui/material';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'Store/index';

import { dialogActions, dialogCancel, dialogContent, dialogTitle } from '../../config/theme/componentTheme';
import { actions } from '../../store/documents/actions';

import ActionButton from '../ActionButton';
import FormRow from '../FormRow';
import InputHandler from '../InputHandler';
import { endingReasons } from 'Routes/Medication/Document/Form/Medication';

const DialogPaper = (props: PaperProps): React.JSX.Element => <Paper square={true} {...props} />;

const optionFormatter = (n: string | number): React.JSX.Element => <FormattedMessage id={`medication.opts.${n}`} />;

const MedicationEndReasonDialog = ({ document, openState }: IMedicationEndReasonDialogProps): React.JSX.Element => {
  const platform = useSelector((s: { session?: ISessionStore }) => s.session?.platforms?.selected);

  const [open, setOpen] = openState;

  const { endReason, endReasonPrimary, endReasonOther } = document;

  const [thisFormData, setThisFormData] = React.useState<IEndReason>({ endReason, endReasonPrimary, endReasonOther });
  const [asyncLoading, setAsyncLoading] = React.useState<boolean>(false);

  const onChange = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    const value = values[name] as string[];
    setThisFormData({
      ...thisFormData,
      endReasonPrimary:
        name === 'endReason' && !(Array.isArray(value) ? value : []).includes(thisFormData.endReasonPrimary || '')
          ? undefined
          : thisFormData.endReasonPrimary,
      [name]: value,
    });
  };

  const dispatch = useDispatch();
  const saveHandler = async () => {
    setAsyncLoading(true);
    if (document._id) {
      await actions.modifyField({ name: 'medication', id: document._id }, document, thisFormData)(dispatch);
      setOpen(false);
    }
    setAsyncLoading(false);
  };

  return (
    <Dialog open={open} maxWidth={'md'} fullWidth={true} PaperComponent={DialogPaper}>
      <DialogTitle style={dialogTitle}>
        <FormattedMessage id={'medication.endedReason'} />
      </DialogTitle>
      <DialogContent style={dialogContent}>
        <FormRow title="medication.endedReason">
          <InputHandler
            type="Checkbox"
            editing={true}
            name="endReason"
            formData={{ document: thisFormData, onChange: onChange }}
            options={endingReasons(thisFormData.endReason || [], platform)}
            optionFormatter={optionFormatter}
          />
        </FormRow>
        <FormRow
          title="medication.endedReasonPrimary"
          condition={thisFormData.endReason && thisFormData.endReason?.length > 1}
        >
          <InputHandler
            type="Select"
            editing={true}
            name="endReasonPrimary"
            formData={{ document: thisFormData, onChange: onChange }}
            options={thisFormData.endReason || []}
            optionFormatter={optionFormatter}
            placeholder="medication.endedReasonPrimaryPlaceholder"
          />
        </FormRow>
        <FormRow title="medication.endedReasonOther">
          <InputHandler
            type="TextArea"
            editing={true}
            name="endReasonOther"
            formData={{
              document: thisFormData,
              onChange: onChange,
            }}
            placeholder={'medication.pausationAdditionalInfoPlaceholder'}
          />
        </FormRow>
      </DialogContent>
      <DialogActions style={dialogActions}>
        <div style={dialogCancel} onClick={(): void => setOpen(false)}>
          <FormattedMessage id="general.cancel" />
        </div>
        <ActionButton
          text="general.save"
          onClick={saveHandler}
          width={15}
          height={4}
          fontSize={16}
          loading={asyncLoading}
          disabled={!thisFormData.endReason || thisFormData.endReason.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};

interface IEndReason extends IMedicationEndingReason {
  _id?: string;
}

interface IMedicationEndReasonDialogProps {
  document: IMedication;
  openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

export default MedicationEndReasonDialog;
